/* eslint-disable max-lines-per-function */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, CheckCircle } from 'lucide-react';
import Footer from '../../components/Footer';
import { useAuth } from '../components/AuthProviderWrapper';
import { isIOSDevice } from '../utils/deviceDetection';
import { FeatureCard, BenefitCard, TestimonialCard } from './LandingPage.components';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const isIOS = isIOSDevice();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/app/recipes', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleGetStarted = () => navigate('/onboarding');
  const handleLogin = () => navigate('/auth/login');
  const handleSignUp = () => navigate('/onboarding');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6651]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header 
        className="fixed w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100"
        style={{ 
          top: 0,
          paddingTop: isIOS ? 'env(safe-area-inset-top, 0px)' : '0'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <img src="/dinnerbrain-logo-proper.svg" alt="DinnerBrain Logo" className="h-10 sm:h-12 w-auto" />
              <span className="text-xl sm:text-2xl font-bold text-[#4A6651] hidden sm:block" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                DinnerBrain
              </span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={handleLogin}
                className="px-3 sm:px-5 py-2 text-sm sm:text-base text-[#4A6651] font-medium border border-[#4A6651] rounded-lg hover:bg-[#4A6651] hover:text-white transition-colors"
              >
                Log In
              </button>
              <button
                onClick={handleSignUp}
                className="px-3 sm:px-5 py-2 text-sm sm:text-base bg-[#4A6651] text-white font-medium rounded-lg hover:bg-[#3d5444] transition-colors"
              >
                End Dinner Stress Today
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="pb-16 px-4 sm:px-6 lg:px-8"
        style={{ paddingTop: isIOS ? 'calc(7rem + env(safe-area-inset-top, 0px))' : '7rem' }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <img 
            src="/dinner-brain-overwhelmed.png" 
            alt="DinnerBrain - Your meal planning assistant" 
            className="w-64 sm:w-80 md:w-96 h-auto mx-auto mb-8"
          />
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Take dinner decision fatigue off your plate.
          </h1>
          <p className="text-lg sm:text-2xl text-gray-600 mb-4">
            Save the meals you already trust, turn them into a weekly plan, and generate the grocery list.
          </p>
          
          {/* YouTube Video Embed */}
          <div className="mb-8 max-w-3xl mx-auto">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              {/* @security-safe: YouTube embed with restrictive sandbox */}
              <iframe 
                className="absolute top-0 left-0 w-full h-full rounded-xl shadow-lg"
                src="https://www.youtube.com/embed/j4KLmdzb5zU?start=1"
                title="DinnerBrain Introduction Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
                loading="lazy"
              />
            </div>
          </div>
          
          <p className="text-base sm:text-lg text-gray-500 mb-8 max-w-2xl mx-auto">
            No more reopening the dinner debate every day. Transform your recipes into a calmer plan for the week.
          </p>
          <button
            onClick={handleGetStarted}
            className="inline-flex items-center px-8 py-4 bg-[#4A6651] text-white text-lg font-semibold rounded-xl hover:bg-[#3d5444] transition-colors shadow-lg"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            Start Risk-Free
          </button>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            How It Works
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg">3 Simple Steps to Dinner Success</p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              number="1"
              title="Save Your Recipes"
              description="Add your favorite recipes from anywhere—typed in, pasted, or imported via URL. Keep them organized and always at your fingertips."
            />
            <FeatureCard
              number="2"
              title="Plan Your Week in Seconds"
              description="Add recipes in a snap! Plan your week of meals, or let DinnerBrain surprise you. Dinner made easy, fun, and stress-free!"
            />
            <FeatureCard
              number="3"
              title="Shop Smarter"
              description="Automatically generate a categorized grocery list from your meal plan. Add or remove items on the fly. Share it with anyone."
            />
          </div>
        </div>
      </section>

      {/* Why People Love DinnerBrain */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <img 
            src="/dinnerbrain-kawaii-logo-love.png" 
            alt="DinnerBrain Love" 
            className="w-32 sm:w-40 md:w-48 h-auto mx-auto mb-8"
          />
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Why People Love DinnerBrain
          </h2>
          <div className="space-y-6">
            <BenefitCard
              title="Build Your Recipe Collection, Your Way"
              description="Keep the recipes you already trust in one organized place, instead of scattered across links, screenshots, cards, and notes."
            />
            <BenefitCard
              title="Stress-Free Meal Planning"
              description="Skip the daily 'what's for dinner?' struggle. Add recipes in seconds, share or print your plan, or let DinnerBrain create one for you when inspiration runs out."
            />
            <BenefitCard
              title="Smarter Shopping, Done for You"
              description="Every meal instantly builds your shopping list. No more forgotten ingredients or wasted time. Everything you need, all in one place."
            />
            <BenefitCard
              title="Save Hours Every Week"
              description="Turn meal planning from a time-consuming chore into a quick, simple step that takes minutes, not hours."
            />
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <img 
            src="/dinnerbrain-kawaii-logo-iphone.png" 
            alt="DinnerBrain Reviews" 
            className="w-32 sm:w-40 md:w-48 h-auto mx-auto mb-8"
          />
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            What Our Users Say
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <TestimonialCard text="DinnerBrain completely changed how I meal plan." />
            <TestimonialCard text="This is the solution I have been looking for." />
          </div>
        </div>
      </section>

      {/* Mobile First Experience */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <img 
            src="/dinnerbrain-kawaii-logo-iphone-pan.png" 
            alt="Mobile Experience" 
            className="w-32 sm:w-40 md:w-48 h-auto mx-auto mb-8"
          />
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            📲 Mobile First Experience
          </h2>
          <p className="text-xl text-gray-600 mb-4">
            DinnerBrain works on iOS, and the web seamlessly. Android app coming soon!
          </p>
          <p className="text-lg text-gray-500">
            Log in anywhere, plan and cook from your pocket.
          </p>
        </div>
      </section>

      {/* Community Recipes */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            👩‍🍳 Community Recipes
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Discover what other food lovers are cooking in our Community Recipes! Explore dishes shared by fellow members, get inspired with new ideas, and easily add your favorites to your own DinnerBrain account. You can even follow other users to keep up with their latest creations and grow your own recipe network. It&apos;s the perfect way to expand your meal options while connecting with a community that loves good food as much as you do.
          </p>
          <button
            onClick={() => {
              // Always use app route structure for consistency
              navigate('/app/community');
            }}
            className="inline-flex items-center px-8 py-4 bg-[#4A6651] text-white text-lg font-semibold rounded-xl hover:bg-[#3d5444] transition-colors shadow-lg"
          >
            Explore Community Recipes
          </button>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#4A6651]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            🚀 Ready to make dinner easier?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Start using DinnerBrain free today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleGetStarted}
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#4A6651] text-lg font-semibold rounded-xl hover:bg-gray-100 transition-colors shadow-lg"
            >
              Try DinnerBrain Today — Cancel Anytime
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
            <button
              onClick={() => navigate('/knowledge-base')}
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white text-lg font-semibold rounded-xl hover:bg-white/10 transition-colors"
            >
              See How It Works
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;